/* Lịch sử chia lại khách (Telesales + chat Trực page) — có lọc, phân trang, xuất Excel */
import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button, DatePicker, Empty, Input, Segmented, Select, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { type Dayjs } from "dayjs";
import * as XLSX from "xlsx";
import { ArrowLeft, ArrowRight, Bot, Download, MessageCircle, Phone, Search, Shuffle, UserCog } from "lucide-react";
import Layout from "../../Layout";
import useAuthStore from "../../store/authStore";
import { Kpi } from "./dashboard/ui";
import { idleText } from "./lead/reassign";
import { useLeadStore, type ReassignLog } from "./lead/store";

type Kind = "all" | "call" | "chat";
type Mode = "all" | "auto" | "manual";

const kindOf = (x: ReassignLog) => x.kind ?? "call";

export default function ReassignHistory() {
    const { isSuperAdmin, hasPermission } = useAuthStore();
    const canManage = isSuperAdmin || hasPermission("telesales.assign") || hasPermission("telesales.config");
    const log = useLeadStore((s) => s.reassignLog);

    const [kind, setKind] = useState<Kind>("all");
    const [mode, setMode] = useState<Mode>("all");
    const [q, setQ] = useState("");
    const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
    const [to, setTo] = useState<string>();
    const [from, setFrom] = useState<string>();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const people = useMemo(() => ({
        to: Array.from(new Set(log.map((x) => x.to))).sort().map((v) => ({ value: v, label: v })),
        from: Array.from(new Set(log.map((x) => x.from ?? "Chưa phân"))).sort().map((v) => ({ value: v, label: v })),
    }), [log]);

    const rows = useMemo(() => {
        const kw = q.trim().toLowerCase();
        const [d0, d1] = range ?? [];
        return log.filter((x) => (kind === "all" || kindOf(x) === kind)
            && (mode === "all" || x.mode === mode)
            && (!to || x.to === to)
            && (!from || (x.from ?? "Chưa phân") === from)
            && (!d0 || x.at >= d0.startOf("day").valueOf())
            && (!d1 || x.at <= d1.endOf("day").valueOf())
            && (!kw || [x.customer, x.from, x.to, x.by].some((v) => String(v ?? "").toLowerCase().includes(kw))));
    }, [log, kind, mode, to, from, range, q]);

    const stats = {
        total: rows.length,
        call: rows.filter((x) => kindOf(x) === "call").length,
        chat: rows.filter((x) => kindOf(x) === "chat").length,
        auto: rows.filter((x) => x.mode === "auto").length,
    };
    const filtered = kind !== "all" || mode !== "all" || !!to || !!from || !!range || !!q.trim();
    const reset = () => { setKind("all"); setMode("all"); setTo(undefined); setFrom(undefined); setRange(null); setQ(""); setPage(1); };
    // Đổi bộ lọc → về trang 1
    const f = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPage(1); };

    const exportExcel = () => {
        const sheet = XLSX.utils.json_to_sheet(rows.map((x) => ({
            "Thời gian": dayjs(x.at).format("DD/MM/YYYY HH:mm"),
            "Khách": x.customer,
            "Mã khách": x.customerId,
            "Loại": kindOf(x) === "chat" ? "Chat · Trực page" : "Gọi điện · Telesales",
            "Từ": x.from ?? "Chưa phân",
            "Sang": x.to,
            "Thời gian chờ": idleText(x.idle),
            "Cách chia": x.mode === "auto" ? "Tự động" : "Thủ công",
            "Người thực hiện": x.mode === "auto" ? "Hệ thống" : x.by,
        })));
        const book = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(book, sheet, "Lich su chia lai");
        XLSX.writeFile(book, `lich-su-chia-lai-${dayjs().format("YYYYMMDD-HHmm")}.xlsx`);
    };

    const columns: ColumnsType<ReassignLog> = [
        {
            title: "THỜI GIAN",
            width: 140,
            sorter: (a, b) => a.at - b.at,
            defaultSortOrder: "descend",
            render: (_, x) => (
                <div className="leading-tight">
                    <div className="font-semibold tabular-nums text-gray-800 dark:text-gray-100">{dayjs(x.at).format("HH:mm")}</div>
                    <div className="text-[12px] text-gray-400">{dayjs(x.at).format("DD/MM/YYYY")}</div>
                </div>
            ),
        },
        {
            title: "KHÁCH",
            render: (_, x) => (
                <div className="leading-tight">
                    <div className="font-semibold text-gray-800 dark:text-gray-100">{x.customer}</div>
                    <div className="text-[12px] text-gray-400">#{x.customerId}</div>
                </div>
            ),
        },
        {
            title: "LOẠI",
            width: 150,
            render: (_, x) => (kindOf(x) === "chat"
                ? <Tag color="orange" className="m-0 inline-flex items-center gap-1"><MessageCircle size={12} />Chat · Trực page</Tag>
                : <Tag color="cyan" className="m-0 inline-flex items-center gap-1"><Phone size={12} />Gọi · Telesales</Tag>),
        },
        {
            title: "CHIA TỪ → SANG",
            render: (_, x) => (
                <span className="inline-flex flex-wrap items-center gap-1.5">
                    <span className={x.from ? "text-gray-600 dark:text-gray-300" : "text-orange-500"}>{x.from ?? "Chưa phân"}</span>
                    <ArrowRight size={14} className="text-gray-400" />
                    <b className="text-[#2c6b7f] dark:text-[#8cc8da]">{x.to}</b>
                </span>
            ),
        },
        {
            title: "THỜI GIAN CHỜ",
            width: 130,
            sorter: (a, b) => a.idle - b.idle,
            render: (_, x) => <span className="font-semibold text-red-600">{idleText(x.idle)}</span>,
        },
        {
            title: "CÁCH CHIA",
            width: 170,
            render: (_, x) => (x.mode === "auto"
                ? <span className="inline-flex items-center gap-1.5 text-[#2c6b7f] dark:text-[#8cc8da]"><Bot size={14} />Tự động</span>
                : <span className="inline-flex items-center gap-1.5 text-gray-700 dark:text-gray-300"><UserCog size={14} />{x.by}</span>),
        },
    ];

    return (
        <Layout>
            <Helmet><title>Lịch sử chia lại | Khothi</title></Helmet>
            <div className="space-y-4 pb-16 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                        <Link to="/telesales/customer" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#e8f4f7] text-[#3e91ab] hover:bg-[#d5ebf1] dark:bg-[#3e91ab]/20" aria-label="Về Khách tiềm năng">
                            <ArrowLeft size={18} />
                        </Link>
                        <div className="min-w-0">
                            <h1 className="m-0 text-[18px] font-bold leading-tight text-gray-900 dark:text-gray-50">Lịch sử chia lại khách</h1>
                            <div className="text-[12.5px] text-gray-500 dark:text-gray-400">Các lần chia lại vì Telesales không tương tác hoặc Trực page chưa trả lời chat</div>
                        </div>
                    </div>
                    <Button icon={<Download size={14} />} disabled={!rows.length} onClick={exportExcel}>Xuất Excel</Button>
                </div>

                {!canManage ? (
                    <div className="rounded-xl border border-gray-200 bg-white p-10 dark:border-gray-700 dark:bg-gray-800">
                        <Empty description="Chỉ Người vận hành xem được lịch sử chia lại" />
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
                            <Kpi icon={<Shuffle size={15} />} label="Lần chia lại" value={stats.total} sub={filtered ? "Theo bộ lọc" : "Tất cả"} />
                            <Kpi icon={<Phone size={15} />} tone="sky" label="Gọi điện · Telesales" value={stats.call} onClick={() => f(setKind)(kind === "call" ? "all" : "call")} active={kind === "call"} />
                            <Kpi icon={<MessageCircle size={15} />} tone="amber" label="Chat · Trực page" value={stats.chat} onClick={() => f(setKind)(kind === "chat" ? "all" : "chat")} active={kind === "chat"} />
                            <Kpi icon={<Bot size={15} />} tone="indigo" label="Tự động" value={stats.auto} sub={stats.total ? `${Math.round((stats.auto / stats.total) * 100)}% số lần chia` : undefined} onClick={() => f(setMode)(mode === "auto" ? "all" : "auto")} active={mode === "auto"} />
                        </div>

                        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                            <div className="mb-3 flex flex-wrap items-center gap-2">
                                <Input allowClear prefix={<Search size={14} className="text-gray-400" />} placeholder="Tìm khách, nhân viên" value={q} onChange={(e) => f(setQ)(e.target.value)} className="w-[220px]" />
                                <Segmented value={kind} onChange={(v) => f(setKind)(v as Kind)} options={[{ value: "all", label: "Tất cả" }, { value: "call", label: "Gọi điện" }, { value: "chat", label: "Chat" }]} />
                                <Select value={mode} onChange={f(setMode)} options={[{ value: "all", label: "Mọi cách chia" }, { value: "auto", label: "Tự động" }, { value: "manual", label: "Thủ công" }]} className="w-[150px]" />
                                <DatePicker.RangePicker value={range as any} onChange={(v) => f(setRange)(v as any)} format="DD/MM/YYYY" placeholder={["Từ ngày", "Đến ngày"]} />
                                <Select allowClear showSearch placeholder="Chia từ" value={from} onChange={f(setFrom)} options={people.from} className="w-[180px]" />
                                <Select allowClear showSearch placeholder="Chia sang" value={to} onChange={f(setTo)} options={people.to} className="w-[180px]" />
                                {filtered && <Button type="link" onClick={reset}>Xoá lọc</Button>}
                            </div>
                            <Table
                                rowKey="id"
                                columns={columns}
                                dataSource={rows}
                                size="middle"
                                scroll={{ x: 900 }}
                                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={filtered ? "Không có lần chia lại nào khớp bộ lọc" : "Chưa có lần chia lại nào"} /> }}
                                pagination={{
                                    current: page,
                                    pageSize,
                                    total: rows.length,
                                    showSizeChanger: true,
                                    pageSizeOptions: [20, 50, 100],
                                    showTotal: (t, [a, b]) => `${a}–${b} / ${t} lần chia`,
                                    onChange: (p, s) => { setPage(s !== pageSize ? 1 : p); setPageSize(s); },
                                }}
                            />
                        </div>
                    </>
                )}
            </div>
        </Layout>
    );
}
